import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { rememberImageSizes } from "@/components/agent-session/sessionInlineImages";
import type { AssistantSession, StreamMessage } from "../types";
import type { ThreadsStore } from "./threadsStore";
import { useLatestRef } from "@/hooks/useLatestRef";

/**
 * Messages in the initial windowed session snapshot and the first older page.
 * Each further page doubles, up to MAX_EARLIER_PAGE_SIZE: folded review,
 * push and Proof rounds keep a long transcript short, so 15-message pages
 * chain dozens of times on open and regroup the rows on screen each time.
 */
export const MESSAGE_PAGE_SIZE = 15;
const MAX_EARLIER_PAGE_SIZE = 120;

/** Asks message reads for attachment URLs instead of inline base64. */
export const ATTACHMENTS_AS_URLS = "url";

interface EarlierPageResponse {
  session_id: string;
  messages: StreamMessage[];
  image_sizes?: AssistantSession["image_sizes"];
  has_more: boolean;
}

/**
 * Paging state for one session's loaded older pages. Snapshot refetches keep
 * returning the windowed tail with `messages_has_more: true`, so once a page
 * has been loaded the client-side cursor (older than anything the snapshot
 * carries) is the truth, not the snapshot's flag.
 */
interface PagedState {
  /**
   * Bucket epoch the loaded pages were ingested into. An evicted bucket
   * rehydrates from the snapshot window only, dropping prepended rows, so a
   * changed epoch invalidates the cursor and falls back to the snapshot's.
   */
  bucketEpoch: number;
  cursorId: string | null;
  hasMore: boolean;
  /** Older pages loaded so far; sizes the next one. */
  pages: number;
}

/**
 * Earlier-page loading for the windowed session snapshot: fetches the
 * page before the oldest loaded row and merges it into the thread store,
 * where rows prepend by sort order (`applySnapshot` merges by id and never
 * deletes, so later snapshot refetches keep the prepended rows).
 */
export function useEarlierMessages(
  sessionId: string | null,
  session: AssistantSession | null | undefined,
  store: ThreadsStore,
) {
  const api = useApi();
  // Keyed by session id so switching threads doesn't discard another
  // session's cursor: the store keeps its prepended rows, so on return the
  // cursor (not the snapshot's window-start fallback) is still the truth.
  const [pagedBySession, setPagedBySession] = useState<
    Record<string, PagedState>
  >({});
  // Per session: a request for a backgrounded session keeps running (its
  // response is dropped), and returning to that session must wait for it
  // rather than start a second request whose older twin could land later
  // and regress the cursor.
  const [loadingSessionIds, setLoadingSessionIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const activeSessionIdRef = useLatestRef(sessionId);

  const snapshot = session && session.id === sessionId ? session : null;

  // A snapshot that isn't truncated (`messages_has_more` false or absent:
  // fork responses carry the full copied transcript, short sessions the
  // whole thing) seeds the store with rows back to the transcript start.
  // Recorded as an exhausted cursor because a later windowed refetch of a
  // grown session reports `messages_has_more: true` for rows the store
  // already holds (`applySnapshot` never deletes), which would otherwise
  // resurrect the affordance and page in rows already on screen.
  const snapshotIsComplete =
    snapshot?.messages !== undefined && snapshot.messages_has_more !== true;
  const snapshotFirstMessageId = snapshot?.messages?.[0]?.id ?? null;
  const bucketEpoch = sessionId !== null ? store.bucketEpoch(sessionId) : null;
  const paged = sessionId !== null ? (pagedBySession[sessionId] ?? null) : null;
  // A complete payload whose first row matches a record from an older epoch
  // is a cached repaint, not fresh evidence: eviction dropped the rows it
  // seeded, so re-recording under the current epoch would keep hiding history
  // after the fresh windowed refetch lands. The snapshot fallback stays
  // correct while this payload is current.
  const exhaustedRecordCurrent =
    paged !== null &&
    !paged.hasMore &&
    (paged.bucketEpoch === bucketEpoch ||
      paged.cursorId === snapshotFirstMessageId);
  if (
    sessionId !== null &&
    bucketEpoch !== null &&
    snapshotIsComplete &&
    !exhaustedRecordCurrent
  ) {
    setPagedBySession((prev) => ({
      ...prev,
      [sessionId]: {
        bucketEpoch,
        cursorId: snapshotFirstMessageId,
        hasMore: false,
        pages: 0,
      },
    }));
  }

  const pagedActive =
    paged !== null && paged.bucketEpoch === bucketEpoch ? paged : null;

  const hasEarlierMessages = pagedActive
    ? pagedActive.hasMore
    : (snapshot?.messages_has_more ?? false);
  const cursorId = pagedActive
    ? pagedActive.cursorId
    : (snapshot?.messages?.[0]?.id ?? null);
  const pagesLoaded = pagedActive?.pages ?? 0;
  const pageSize = Math.min(
    MESSAGE_PAGE_SIZE * 2 ** pagesLoaded,
    MAX_EARLIER_PAGE_SIZE,
  );
  const isLoadingEarlier =
    sessionId !== null && loadingSessionIds.has(sessionId);

  // Callers firing in the same commit (the top-of-transcript auto-load and
  // an out-of-window deep link) all still read isLoadingEarlier as false.
  const inFlightSessionIdsRef = useRef(new Set<string>());
  const loadEarlierMessages = useCallback(async () => {
    if (!sessionId || !cursorId) return;
    if (inFlightSessionIdsRef.current.has(sessionId)) return;
    inFlightSessionIdsRef.current.add(sessionId);
    setLoadingSessionIds((prev) => new Set(prev).add(sessionId));
    await api
      .get<EarlierPageResponse>(`/assistant/sessions/${sessionId}/messages`, {
        params: {
          before_message_id: cursorId,
          limit: pageSize,
          attachments: ATTACHMENTS_AS_URLS,
        },
      })
      .then((res) => {
        const page = res.data;
        // Before the rows apply, so their images mount with reserved boxes.
        rememberImageSizes(page.image_sizes);
        // A response landing after a thread switch is dropped: ingesting it
        // would resurrect a backgrounded (possibly just-evicted) session's
        // bucket, and its paging state would validate against that epoch.
        if (activeSessionIdRef.current !== sessionId) return;
        if (page.messages.length > 0) {
          // No stream seq: an older page is not a full snapshot, so it must
          // not advance the store's replay cursor. Rows land at seq 0, where
          // any live write still wins.
          store.applySnapshot(sessionId, page.messages, undefined);
        }
        setPagedBySession((prev) => ({
          ...prev,
          [sessionId]: {
            bucketEpoch: store.bucketEpoch(sessionId),
            cursorId: page.messages[0]?.id ?? cursorId,
            hasMore: page.has_more,
            pages: pagesLoaded + 1,
          },
        }));
      })
      .catch((error: unknown) => {
        // Paging state stays put so the affordance survives for a retry;
        // rethrown so callers (ChatPane's scroll restore) can unwind.
        console.error("Failed to load earlier messages:", error);
        throw error;
      })
      .finally(() => {
        inFlightSessionIdsRef.current.delete(sessionId);
        setLoadingSessionIds((prev) => {
          const next = new Set(prev);
          next.delete(sessionId);
          return next;
        });
      });
  }, [
    activeSessionIdRef,
    api,
    cursorId,
    pageSize,
    pagesLoaded,
    sessionId,
    store,
  ]);

  return { hasEarlierMessages, isLoadingEarlier, loadEarlierMessages };
}
